import { TBasicOption, TSelectOption } from './common.types';
import {
  IChatConfig,
  IMainConfig,
  IPollsConfig,
  IQuestionsConfig,
  IParticipantsListConfig,
  IMagicPenConfig,
} from './settings.types';
import { IAttendee } from '@src/webinar/types';
import { IPeer, IPeerInfo, TUserType, TUserTypeWithoutHost } from './users.types';
import { ISharedCalculatorDetails } from './calculator.type';

export interface IWebinar {
  id: string;
  roomTitle: string;
  creator: string;
  creatorEmail: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  status: EWebinarActiveStatus;
  roomSetting: IWebinarSettings;
  agoraRoomDetails?: null;
}

export interface IWebinarSettings {
  passwordRequired: boolean;
  roomPassword: string | null;
  autoRecordEnabled: boolean;
  registrationRequired: boolean;
  registrationExpiryDate: string | null;
  timeZone?: string;
  requireReminder?: boolean;
  reminders?: Date[];
  questionSettings: {
    maxQuestions: number;
    allowAnonymous: boolean;
    allowDismissQuestion: boolean;
    questionsEnabled: boolean;
    maxThreadReplies?: number;
  };
  ctaSettings: {
    ctaEnabled: boolean;
  };
  calculatorSettings: {
    shareCalculatorEnabled: boolean;
  };
}

export type TWebinarStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface IAttendeesFilters {
  type: TUserTypeWithoutHost[];
  domain: string[];
  country: string[];
  company: string[];
  search: string;
  invitationStatus?: 'declined' | 'pending';
}

export interface ICountryCode extends TSelectOption<string> {
  attributes: {
    countryCode: string;
  };
}

export interface ICountry extends TBasicOption {
  code: string;
}

export interface IAttendeesFiltersProps {
  filters: IUIFilters;
  setFilters: any;
  SelectAll: () => void;
  UnselectAll: () => void;
  selectedAttendees: IAttendee[];
  handleOpenBulkActionModal: any;
  setSelectedAttendees: (attendees: IAttendee[]) => void;
  handleFilterChange: (key: keyof IUIFilters, value: TBasicOption[] | ICountry[]) => void;
}

export interface IUIFilters {
  types: { label: string; value: TUserTypeWithoutHost }[];
  emailDomains: TBasicOption[];
  countries: ICountry[];
  companies: TBasicOption[];
  search: string;
}

export interface IAgoraStreamingData {
  status: boolean;
  token: string;
  userId: string;
  stream_uid: number;
}

export interface IAttendeeUserInfo {
  email?: string;
  firstName?: string;
  lastName?: string;
  id?: string;
  passwordRequired?: boolean;
  status?: TWebinarStatus;
  type: TUserType;
}

export interface IAttendeeDetails {
  actualStartTime: string | null;
  endTime: string;
  id: string;
  roomSetting: IWebinarSettings;
  roomTitle: string;
  startTime: string;
  status: string;
  user: IAttendeeUserInfo;
  hasAnnotations?: boolean;
}

export interface IAttendeeDetailsApiResponse {
  success: boolean;
  body: IAttendeeDetails;
  errorCode?: string;
}

export interface IStreamDetails {
  viewerHLS: string;
  viewerIframeSrc: string;
  agoraConverter?: {
    converterId: string;
    rtmpUrl: string;
    sequence: number;
    activeUids: number[];
    layout: string;
    pinedUserIds: number[];
  };
}

export interface IShareScreenStreamCredentials {
  stream_uid: number;
  token: string;
}

export interface IJoinResponse {
  puid: string;
  success: boolean;
  errorCode?: 'WEBINAR_ENDED' | 'FORBIDDEN' | 'CAPACITY_LIMIT' | 'BANNED' | 'ROOM_NOT_FOUND' | 'USER_NOT_FOUND';
  message?: string;
  accessKey?: string;
  roomConfig?: {
    main: IMainConfig;
    chat: IChatConfig;
    poll: IPollsConfig;
    questions: IQuestionsConfig;
    participantsList: IParticipantsListConfig;
    magicPen?: IMagicPenConfig;
  };
  streamDetails?: IStreamDetails;
  peers?: IPeer[];
  user?: IPeer;
  hasPolls?: boolean;
  userAgoraStreamingDetails?: IAgoraStreamingData;
  calculatorDetails?: ISharedCalculatorDetails | null;
  viewersCount?: string;
}

export enum EWebinarEventsTypes {
  END_SESSION = 'end_session',
  HOST_JOIN = 'HOST_JOIN',
  SHARE_SCREEN = 'shareScreen',
}

export interface IWebinarEvent {
  body: {
    reason: string;
    message: string;
  };
  eventType: EWebinarEventsTypes;
}

export interface IRemoveMeData {
  peer_id: string;
  peer_name: string;
  room_id: string;
  stream_uid: number;
}

export interface IUpdatePeerInfoEvent {
  broadcast: boolean;
  peer_id: string;
  peer_name: string;
  room_id: string;
  status: boolean;
  extraData?: Partial<IPeerInfo>;
  type: 'audio' | 'video';
}

export interface ISocketError {
  errorCode: string;
  message: string;
}

export enum EWebinarActiveStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SCHEDULED = 'SCHEDULED',
}
